
import { Link } from "react-router-dom";
import {
  Home, History, Briefcase, FileText, Phone, MapPin,
  ClipboardList, Map, Instagram, HelpCircle, MessageSquare,
  Route, Sparkles, ArrowRight, ChevronRight,
} from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { FloatingWhatsApp } from "@/components/FloatingWhatsApp";
import { AccessibilityButton } from "@/components/AccessibilityButton";
import { useNavConfig } from "@/hooks/useNavConfig";

// ── Data ───────────────────────────────────────────────────────────────────────

const SECTIONS = [
  {
    title: "Páginas Principais",
    color: "brand",
    items: [
      { icon: Home,        label: "Início",        to: "/inicio",       desc: "Página principal do site" },
      { icon: History,     label: "Quem Somos",    to: "/sobre",        desc: "Nossa história e trajetória" },
      { icon: Briefcase,   label: "Serviços",      to: "/servicos",     desc: "Tipos de atendimento" },
      { icon: FileText,    label: "Blog",          to: "/blog",         desc: "Artigos e dicas de nutrição" },
      { icon: Phone,       label: "Contato",       to: "/contato",      desc: "Fale conosco" },
      { icon: MapPin,      label: "Consultórios",  to: "/consultorios", desc: "Endereços e localização" },
    ],
  },
  {
    title: "Pacientes",
    color: "teal",
    items: [
      { icon: ClipboardList, label: "Avaliação de Caso", to: "/avaliacoes", desc: "Solicite uma avaliação" },
    ],
  },
  {
    title: "Conteúdo",
    color: "violet",
    items: [
      { icon: FileText,    label: "Blog",          to: "/blog",         desc: "Publicações semanais" },
      { icon: Instagram,   label: "Imprensa",      to: "/imprensa",     desc: "Mídia e press kit" },
    ],
  },
];

const HOME_SECTIONS = [
  { icon: Sparkles,     label: "Hero",               desc: "Banner principal e apresentação" },
  { icon: Route,        label: "Jornada do Paciente", desc: "Etapas do atendimento" },
  { icon: Briefcase,    label: "Diferenciais",        desc: "Nossa abordagem clínica" },
  { icon: FileText,     label: "Blog",                desc: "Últimas publicações em destaque" },
  { icon: Instagram,    label: "Instagram Feed",      desc: "Publicações recentes" },
  { icon: HelpCircle,   label: "FAQ",                 desc: "Perguntas frequentes" },
  { icon: MessageSquare,label: "Depoimentos",         desc: "Avaliações de pacientes" },
  { icon: ArrowRight,   label: "Chamada para Ação",   desc: "Agende sua consulta" },
];

// ── Color variants ─────────────────────────────────────────────────────────────

const COLOR: Record<string, { header: string; icon: string; link: string; badge: string }> = {
  brand:  { header: "bg-brand-primary",    icon: "bg-white/15 text-white",              link: "hover:text-brand-primary",   badge: "bg-brand-primary/10 text-brand-primary" },
  teal:   { header: "bg-teal-600",         icon: "bg-white/15 text-white",              link: "hover:text-teal-600",        badge: "bg-teal-50 text-teal-700" },
  violet: { header: "bg-violet-600",       icon: "bg-white/15 text-white",              link: "hover:text-violet-600",      badge: "bg-violet-50 text-violet-700" },
};

// ── Section card ───────────────────────────────────────────────────────────────

function SectionCard({ section }: { section: typeof SECTIONS[0] }) {
  const c = COLOR[section.color];
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      {/* Header band */}
      <div className={`${c.header} px-5 py-4`}>
        <h2 className="text-sm font-bold text-white uppercase tracking-widest">{section.title}</h2>
      </div>

      {/* Items */}
      <ul className="divide-y divide-gray-50">
        {section.items.map(({ icon: Icon, label, to, desc }) => (
          <li key={to}>
            <Link
              to={to}
              className={`flex items-center gap-3 px-5 py-3.5 group transition-colors hover:bg-gray-50/80 ${c.link}`}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${c.badge}`}>
                <Icon className="w-3.5 h-3.5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-800 group-hover:text-inherit transition-colors">{label}</p>
                <p className="text-xs text-gray-400 mt-0.5">{desc}</p>
              </div>
              <ChevronRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-inherit group-hover:translate-x-0.5 transition-all shrink-0" />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Home sections visual map ───────────────────────────────────────────────────

function HomeSectionsMap() {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="bg-gray-800 px-5 py-4">
        <h2 className="text-sm font-bold text-white uppercase tracking-widest">Seções da Página Inicial</h2>
        <p className="text-gray-400 text-xs mt-0.5">Estrutura vertical de cima para baixo</p>
      </div>

      {/* Vertical flow */}
      <div className="p-5">
        <div className="relative">
          {/* Connector line */}
          <div className="absolute left-[19px] top-0 bottom-0 w-0.5 bg-gray-100" />

          <div className="space-y-2">
            {HOME_SECTIONS.map(({ icon: Icon, label, desc }, i) => (
              <div key={label} className="flex items-start gap-3 relative">
                <div className="w-10 h-10 rounded-xl bg-brand-primary/8 border border-brand-primary/15 flex items-center justify-center shrink-0 z-10">
                  <Icon className="w-4 h-4 text-brand-primary" />
                </div>
                <div className="flex-1 bg-gray-50 rounded-xl px-4 py-2.5 border border-gray-100">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold text-gray-300 uppercase tracking-widest w-4">{i + 1}</span>
                    <p className="text-sm font-semibold text-gray-700">{label}</p>
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5 ml-6">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Info card ──────────────────────────────────────────────────────────────────

function InfoCard() {
  return (
    <div className="bg-brand-primary rounded-2xl p-6 text-white relative overflow-hidden">
      <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-white/5" />
      <div className="absolute -bottom-6 right-10 w-24 h-24 rounded-full bg-white/5" />

      <div className="relative space-y-4">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center">
            <Map className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-white font-bold text-base">Informações do Site</p>
            <p className="text-white/50 text-xs">Clínica de Nutrição Equilíbrio</p>
          </div>
        </div>

        <div className="space-y-2 text-sm">
          {[
            { label: "Nutricionista", value: "Dra. Brenda Ramos" },
            { label: "CRN",          value: "3/63492" },
            { label: "CNPJ",         value: "55.373.902/0001-65" },
            { label: "WhatsApp",     value: "(11) 99887-4081" },
          ].map(({ label, value }) => (
            <div key={label} className="flex items-center justify-between border-b border-white/10 pb-2 last:border-0">
              <span className="text-white/50 text-xs uppercase tracking-wide">{label}</span>
              <span className="text-white font-semibold text-xs">{value}</span>
            </div>
          ))}
        </div>

        <p className="text-white/40 text-xs pt-1">
          Desenvolvido por{" "}
          <a href="https://www.finalogica.com.br" target="_blank" rel="noopener noreferrer"
            className="text-white/70 hover:text-white transition-colors underline">
            Finalógica Soluções
          </a>
        </p>
      </div>
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────

const Sitemap = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero */}
      <section className="pt-24 bg-brand-primary py-14">
        <div className="container mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-white/15 text-white/90 text-xs font-semibold px-4 py-1.5 rounded-full mb-5 tracking-wide uppercase">
            <Map className="w-3.5 h-3.5" />
            Navegação
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white font-primary mb-3">
            Mapa do Site
          </h1>
          <p className="text-white/70 text-base max-w-xl mx-auto">
            Encontre rapidamente qualquer seção ou página do nosso site.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-14">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="grid lg:grid-cols-3 gap-6">

            {/* Left column — page sections */}
            <div className="lg:col-span-2 space-y-6">
              <div className="grid sm:grid-cols-2 gap-6">
                {SECTIONS.map(s => <SectionCard key={s.title} section={s} />)}
              </div>
              <HomeSectionsMap />
            </div>

            {/* Right column — info */}
            <div className="space-y-6">
              <InfoCard />

              {/* Quick links */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4">Acesso Rápido</h3>
                <div className="space-y-1">
                  {[
                    { label: "Agendar Consulta", href: "https://wa.me/5511998874081", external: true },
                    { label: "Ver Serviços",       to: "/servicos" },
                    { label: "Ler o Blog",         to: "/blog" },
                    { label: "Falar Conosco",      to: "/contato" },
                  ].map(item => (
                    item.external ? (
                      <a key={item.label} href={item.href} target="_blank" rel="noopener noreferrer"
                        className="flex items-center justify-between px-3 py-2.5 rounded-xl text-sm font-medium text-gray-600 hover:bg-brand-primary/5 hover:text-brand-primary transition-colors group">
                        {item.label}
                        <ChevronRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-brand-primary group-hover:translate-x-0.5 transition-all" />
                      </a>
                    ) : (
                      <Link key={item.label} to={item.to!}
                        className="flex items-center justify-between px-3 py-2.5 rounded-xl text-sm font-medium text-gray-600 hover:bg-brand-primary/5 hover:text-brand-primary transition-colors group">
                        {item.label}
                        <ChevronRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-brand-primary group-hover:translate-x-0.5 transition-all" />
                      </Link>
                    )
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingWhatsApp />
      <AccessibilityButton />
    </div>
  );
};

export default Sitemap;
